import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { useNavLinksContext } from '../contexts/NavLinksContext';
import { useAppContext } from '../contexts/Storage';
import { TransactionItem } from '../models/TransactionItem';
import { TransactionEntity } from '../models/TransactionEntity';
import { TransactionFetchFilter } from '../models/TransactionFetchFilter';
import { EnhancedTransactionForm } from '../components/Transaction';
import NewTransactionEntitySidebar from '../components/TransactionEntity/NewTransactionEntitySidebar';
import {
  useCreateTransaction,
  useUpdateTransaction,
  useGetTransactions,
  CreateTransactionData,
  UpdateTransactionData,
} from '../api/transactions';
import { useRecords } from '../hooks/useRecords';
import { toast } from 'react-toastify';
import bot from '../assets/images/bot.png';
import info from '../assets/images/information.png';
import { formatLocalDateTimeInput } from '../utils/dateTime';
import { useTooltipTour } from '../tours/TooltipTourContext';
import {
  FROM_SETUP_KEY,
  getOnboarding,
  isDemoSession,
  markOnboarding,
} from '../onboarding/onboardingStorage';
import {
  transactionEntitiesService,
  mapApiEntityToTransactionEntity,
} from '../services/transactionEntitiesService';
import { useAIContext } from '../components/AI/AIContext';

const EXAMPLE_EVENTS = [
  {
    label: 'Paid rent 800 cash',
    action: 'Paid',
    object: 'Rent',
    media: 'Cash',
    amount: 800,
    qty: 1,
  },
  {
    label: 'Sold inventory 5000 bank',
    action: 'Sold',
    object: 'Inventory',
    media: 'Bank',
    amount: 5000,
    qty: 10,
  },
  {
    label: 'Purchased inventory 2000 cash',
    action: 'Purchased',
    object: 'Inventory',
    media: 'Cash',
    amount: 2000,
    qty: 1,
  },
];

function namesMatch(left?: string | null, right?: string | null): boolean {
  return (left || '').trim().toLowerCase() === (right || '').trim().toLowerCase();
}

async function findNamedEntity(
  token: string,
  userId: number,
  name: string,
  category: 'action' | 'object' | 'media',
  recordId?: number
): Promise<TransactionEntity | null> {
  if (!recordId) return null;
  const found = await transactionEntitiesService.findTransactionEntities(token, userId, {
    category,
    record_id: recordId,
    limit: 50,
    page: 1,
  });
  const listed = found.data?.data ?? [];
  const matches = listed.filter(
    (item) =>
      namesMatch(item.transaction_entity_name, name) ||
      namesMatch(item.transaction_entity_past_tense, name)
  );
  const canonical = matches.find(
    (item) =>
      namesMatch(item.transaction_entity_past_tense, name) &&
      !!item.transaction_entity_type
  );
  const typed = matches.find((item) => !!item.transaction_entity_type);
  const named = matches.find((item) => namesMatch(item.transaction_entity_name, name));
  const exact = canonical ?? typed ?? named ?? matches[0];
  return exact ? mapApiEntityToTransactionEntity(exact) : null;
}

const NewTransactionPage: React.FC = () => {
  const navigate = useNavigate();
  const { state: locationState } = useLocation() as { state?: { transaction?: TransactionItem } };
  const { recordId: recordIdParam, transactionId: transactionIdParam } = useParams<{
    recordId?: string;
    transactionId?: string;
  }>();
  const recordIdFromUrl = recordIdParam ? Number(recordIdParam) : undefined;
  const transactionId = transactionIdParam ? Number(transactionIdParam) : undefined;
  const { setNavLinks } = useNavLinksContext();
  const { selectedRecord, user, token, setSelectedRecord } = useAppContext();
  const { fetchRecords } = useRecords();
  const { startTour } = useTooltipTour();
  const { openChat } = useAIContext();
  const [isEditMode, setIsEditMode] = useState(false);
  const [transactionToEdit, setTransactionToEdit] = useState<TransactionItem | null>(null);

  // Use global selected record for new transactions, URL param for edit mode
  const selectedRecordId = isEditMode && recordIdFromUrl ? recordIdFromUrl : selectedRecord?.id;
  const recordName = isEditMode && recordIdFromUrl ? undefined : selectedRecord?.record_name;

  // Create and update mutations
  const createTransactionMutation = useCreateTransaction();
  const updateTransactionMutation = useUpdateTransaction();

  // Fetch single transaction when in edit mode without location state (e.g. refresh)
  const editFilters = useMemo(() => {
    const f = new TransactionFetchFilter();
    if (recordIdFromUrl !== undefined) f.record_id = recordIdFromUrl;
    if (transactionId !== undefined) f.id = transactionId;
    f.limit = 1;
    f.page = 1;
    return f;
  }, [recordIdFromUrl, transactionId]);

  const { data: editTransactionData } = useGetTransactions({
    filters: editFilters,
    enabled: isEditMode && !!recordIdFromUrl && !!transactionId && !locationState?.transaction,
  });

  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedField, setSelectedField] = useState<'action' | 'direct_object' | 'indirect_object' | 'paymentMedia'>('action');
  const [formData, setFormData] = useState<Partial<TransactionItem>>({});
  const [selectedExample, setSelectedExample] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Track if form is searching (for analyzing indicator)
  const [isFormSearching, setIsFormSearching] = useState(false);

  // Set navigation links for the New Transaction page
  useEffect(() => {
    const links = [
      { to: '/dashboard', label: 'Dashboard' },
      { to: '/records', label: 'Books' },
      ...(selectedRecord ? [{ to: '/records/transactions', label: selectedRecord.record_name || 'Transactions' }] : []),
    ];

    setNavLinks({ links });
  }, [setNavLinks, selectedRecord]);

  // If they open New Transaction without books selected, pick the first set
  useEffect(() => {
    if (transactionId || selectedRecord || !user?.id) return;
    let cancelled = false;
    fetchRecords().then((result) => {
      if (cancelled) return;
      if (result.success && result.data?.[0]) {
        setSelectedRecord(result.data[0]);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [transactionId, selectedRecord, user?.id, fetchRecords, setSelectedRecord]);

  // Edit mode: set when we have transactionId in URL
  useEffect(() => {
    setIsEditMode(!!transactionId);
  }, [transactionId]);

  // Load transaction to edit: from location state (navigate from list) or from API (refresh)
  useEffect(() => {
    if (!transactionId) {
      setTransactionToEdit(null);
      return;
    }
    if (locationState?.transaction && locationState.transaction.id === transactionId) {
      setTransactionToEdit(locationState.transaction);
      return;
    }
    if (editTransactionData?.transactions?.length) {
      setTransactionToEdit(editTransactionData.transactions[0]);
    }
  }, [transactionId, locationState?.transaction, editTransactionData?.transactions]);

  const buildTransactionPayload = (
    transactionData: Partial<TransactionItem>
  ): CreateTransactionData | UpdateTransactionData | null => {
    if (
      !transactionData.dateTime ||
      !transactionData.action?.id ||
      !transactionData.direct_object?.id ||
      !transactionData.paymentMedia?.id ||
      transactionData.amount === undefined ||
      !selectedRecordId
    ) {
      return null;
    }
    const formattedDate = transactionData.dateTime.includes('T')
      ? transactionData.dateTime.replace('T', ' ').slice(0, 19)
      : transactionData.dateTime;
    const dateStr = formattedDate.length === 19 ? formattedDate : formattedDate + ':00';

    return {
      transaction_record_id: selectedRecordId,
      transaction_date: dateStr,
      transaction_action_id: transactionData.action.id,
      transaction_object_id: transactionData.direct_object.id,
      transaction_indirect_object_id: transactionData.indirect_object?.id ?? null,
      transaction_media_id: transactionData.paymentMedia.id,
      transaction_quantity: transactionData.qty ?? 1,
      transaction_total_amount: transactionData.amount,
    };
  };

  const handleCreateTransaction = async (transactionData: Partial<TransactionItem>) => {
    if (isSaving) return;
    if (!selectedRecordId) {
      toast.error('Select your books first, then save.');
      return;
    }
    if (!token || !user?.id) {
      toast.error('You need to be signed in to save.');
      return;
    }

    setIsSaving(true);

    let resolved = transactionData;
    try {
      const [action, direct_object, paymentMedia] = await Promise.all([
        transactionData.action?.id || !transactionData.action?.transaction_entity_name
          ? Promise.resolve(transactionData.action)
          : findNamedEntity(
              token,
              user.id,
              transactionData.action.transaction_entity_name,
              'action',
              selectedRecordId
            ).then((found) => found ?? transactionData.action),
        transactionData.direct_object?.id || !transactionData.direct_object?.transaction_entity_name
          ? Promise.resolve(transactionData.direct_object)
          : findNamedEntity(
              token,
              user.id,
              transactionData.direct_object.transaction_entity_name,
              'object',
              selectedRecordId
            ).then((found) => found ?? transactionData.direct_object),
        transactionData.paymentMedia?.id || !transactionData.paymentMedia?.transaction_entity_name
          ? Promise.resolve(transactionData.paymentMedia)
          : findNamedEntity(
              token,
              user.id,
              transactionData.paymentMedia.transaction_entity_name,
              'media',
              selectedRecordId
            ).then((found) => found ?? transactionData.paymentMedia),
      ]);
      resolved = { ...resolved, action, direct_object, paymentMedia };
    } catch (error) {
      setIsSaving(false);
      toast.error(error instanceof Error ? error.message : 'Could not match the words in this event.');
      return;
    }

    const apiData = buildTransactionPayload(resolved);
    if (!apiData) {
      setIsSaving(false);
      toast.error('Use + beside a new word to add it, then save.');
      return;
    }

    if (isEditMode && transactionId) {
      updateTransactionMutation.mutate(
        { transactionId, data: apiData },
        {
          onSuccess: () => {
            navigate('/records/transactions');
          },
          onSettled: () => setIsSaving(false),
        }
      );
      return;
    }

    createTransactionMutation.mutate(
      { data: apiData },
      {
        onSuccess: () => {
          markOnboarding(user?.id, 'addedEvent');
          navigate('/records/transactions');
        },
        onSettled: () => setIsSaving(false),
      }
    );
  };

  const handleCancel = () => {
    navigate('/records/transactions');
  };

  const handleAddEntity = (field: 'action' | 'direct_object' | 'indirect_object' | 'paymentMedia') => {
    setSelectedField(field);
    setSidebarOpen(true);
  };

  const handleSelectEntity = (entity: TransactionEntity) => {
    setFormData(prev => ({ ...prev, [selectedField]: entity }));
    setSidebarOpen(false);
  };

  const handleFormDataChange = useCallback((newFormData: Partial<TransactionItem>) => {
    setFormData({ ...newFormData });
  }, []);

  const applyExample = (example: typeof EXAMPLE_EVENTS[number]) => {
    const now = new Date();
    const localDateTime = formatLocalDateTimeInput(now);

    setSelectedExample(example.label);
    setFormData((prev) => ({
      ...prev,
      dateTime: prev.dateTime || localDateTime,
      action: new TransactionEntity({ transaction_entity_name: example.action }),
      direct_object: new TransactionEntity({ transaction_entity_name: example.object }),
      paymentMedia: new TransactionEntity({ transaction_entity_name: example.media }),
      amount: example.amount,
      qty: example.qty,
    }));

    if (!token || !user?.id) return;

    void Promise.all([
      findNamedEntity(token, user.id, example.action, 'action', selectedRecordId),
      findNamedEntity(token, user.id, example.object, 'object', selectedRecordId),
      findNamedEntity(token, user.id, example.media, 'media', selectedRecordId),
    ]).then(([action, direct_object, paymentMedia]) => {
      setFormData((prev) => ({
        ...prev,
        action: action ?? prev.action,
        direct_object: direct_object ?? prev.direct_object,
        paymentMedia: paymentMedia ?? prev.paymentMedia,
      }));
    });
  };

  useEffect(() => {
    if (isEditMode || isDemoSession() || !user?.id) return;
    if (getOnboarding(user.id).firstRunTourDone) return;
    if (sessionStorage.getItem(FROM_SETUP_KEY) !== '1') return;
    const timer = window.setTimeout(() => startTour('first-run'), 500);
    return () => window.clearTimeout(timer);
  }, [isEditMode, startTour, user?.id]);

  // Show message if no record selected and not in edit mode
  if (!isEditMode && !selectedRecord) {
    return (
      <div className="h-shink-from-navbar h-fill-from-navbar pl-2 bg-bg_grey_light text-dark overflow-y-scroll">
        <div className="bg-light rounded-lg p-8 text-center">
          <p className="text-grey_dark mb-4">Select your books first, then add a sale or expense.</p>
          <Link
            to="/records"
            className="text-primary hover:text-primary_dark underline"
          >
            Go to Books
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="h-shink-from-navbar h-fill-from-navbar pl-2 bg-bg_grey_light text-dark overflow-y-scroll">
      <div className="">

        {/* Two-column layout for desktop, stacked for mobile */}
        <div className="flex flex-col lg:flex-row gap-2 ">
          {/* Left column: Transaction form - full width on mobile, 3/5 on desktop when sidebar is open */}
          <div
            className={`bg-light rounded-t-lg p-6 transition-all duration-300 ease-out ${sidebarOpen
              ? 'lg:w-3/5'
              : 'w-full'
              }`}
          >

            {/* Page Header */}
            <div className=" flex flex-col sm:flex-row justify-between items-start sm:items-center mb-2">
              <div className="w-full flex items-center justify-between mb-2">
                <div>
                  <h3 className="text-xl">{isEditMode ? 'Edit Transaction' : 'New Transaction'}</h3>
                  {recordName && (
                    <p className="text-sm text-grey_dark mt-1">Books: {recordName}</p>
                  )}
                </div>

                {/* Analyzing Container - shows when form is searching */}
                {isFormSearching && (
                  <div className="p-2 md:px-6 border border-grey_light rounded-lg text-center flex justify-between items-center">
                    <div className="text-sm text-secondary animate-pulse">
                      Searching...
                    </div>
                    <img
                      src={bot}
                      alt='bot'
                      className='w-8 h-8 rounded-circle ml-2'
                    />
                  </div>
                )}

              </div>
            </div>

            <hr className="flex-grow border-t border-grey_light mb-4" />

            {!isEditMode && (
              <div
                data-tour="txn-examples"
                className="mb-6 rounded-lg bg-secondary_light border-l-4 border-primary px-4 py-3"
              >
                <p className="text-xs font-semibold uppercase tracking-wide text-primary_variant mb-1">
                  Examples
                </p>
                <p className="text-sm text-dark mb-3">
                  Select an example to fill the form. Edit the amounts afterwards so they match your figures.
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                  {EXAMPLE_EVENTS.map((example) => {
                    const isSelected = selectedExample === example.label;
                    return (
                      <button
                        key={example.label}
                        type="button"
                        onClick={() => applyExample(example)}
                        className={`text-left text-sm px-3 py-2 rounded-md border transition-colors ${
                          isSelected
                            ? 'bg-primary text-white border-primary'
                            : 'bg-light text-dark border-grey_light hover:border-primary'
                        }`}
                      >
                        <span className="block font-medium">
                          {example.action} {example.object}
                        </span>
                        <span className={`block text-xs mt-0.5 ${isSelected ? 'text-white/90' : 'text-grey_dark'}`}>
                          {example.amount} · {example.media}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <button
                  type="button"
                  onClick={() => openChat('Paid rent 800 cash')}
                  className="mt-3 text-sm px-3 py-2 rounded-md border bg-light text-dark border-grey_light hover:border-primary"
                >
                  Ask Balancio AI: Paid rent 800 cash
                </button>
              </div>
            )}

            <EnhancedTransactionForm
              formData={formData}
              onFormDataChange={handleFormDataChange}
              onSubmit={handleCreateTransaction}
              onCancel={handleCancel}
              transaction={transactionToEdit || undefined}
              isEditMode={isEditMode}
              isSubmitting={isSaving || createTransactionMutation.isPending || updateTransactionMutation.isPending}
              onAddEntity={handleAddEntity}
              onSearchStateChange={setIsFormSearching}
            />

            <hr className="flex-grow border-t border-grey_light my-8" />


            {/* Help Section */}
            <div className="mt-8 bg-light rounded-lg border-grey-light border p-3 text-grey_dark">
              <h4 className="flex items-center text-lg mb-3">How to write an event
                <img
                  src={info}
                  alt='info'
                  className='w-4 h-4 mx-2'
                />
              </h4>
              <hr className="flex-grow border-t border-grey_light mb-4" />
              <div className="space-y-2 text-sm text-grey_dark">
                <p>Record one transaction: what happened, the item, and how it was paid.</p>
                <p className="text-xs">If a term is new, use + beside that field, then save.</p>
              </div>
            </div>
          </div>

          {/* Right column: Entity sidebar or empty state */}
          <div className={`transition-all duration-300 ease-out ${sidebarOpen ? 'lg:w-2/5' : 'lg:w-0 hidden lg:block'
            }`}>
            {sidebarOpen ? (
              <NewTransactionEntitySidebar
                isOpen={sidebarOpen}
                onClose={() => setSidebarOpen(false)}
                onSelectEntity={handleSelectEntity}
              />
            ) : (
              <>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewTransactionPage;
